import { createSlice, current } from "@reduxjs/toolkit";

const initialState = {
  /** Last submitted search body — reused for pagination and refine. */
  request: null,
  data: [],
  total: 0,
  currentPage: 0,
  totalPages: 0,
  appliedFilters: {},
  /** Ready-to-render banner line; rendered as-is, never reconstructed client-side. */
  summary: "",
  isLoaded: false,
  isLoading: false,
  isLoadingMore: false,
  error: null,
  loadMoreError: null,
};

/**
 * @param {Object} state
 * @param {Object} response
 * @returns {Object}
 */
const applyResponse = (state, response) => ({
  ...state,
  data: Array.isArray(response?.data) ? response.data : [],
  total: response?.total ?? 0,
  currentPage: response?.currentPage ?? 0,
  totalPages: response?.totalPages ?? 0,
  appliedFilters: response?.appliedFilters ?? {},
  summary: response?.summary ?? "",
  isLoaded: true,
  isLoading: false,
  error: null,
});

const GiftFinderSlice = createSlice({
  name: "GiftFinderSlice",
  initialState,
  reducers: {
    /**
     * Seed from the server-rendered first page — no network call.
     * @param {Object} state
     * @param {Object} action
     */
    hydrateGiftFinderResultsAction: (state, action) => ({
      ...applyResponse(state, action.payload?.response),
      request: action.payload?.request ?? null,
    }),

    /**
     * @param {Object} state
     * @param {Object} action
     */
    requestGiftFinderResultsAction: (state, action) => ({
      ...state,
      request: action.payload?.body ?? state.request,
      isLoading: true,
      error: null,
    }),

    /**
     * @param {Object} state
     * @param {Object} action
     */
    responseGiftFinderResultsAction: (state, action) => ({
      ...applyResponse(state, action.payload?.response),
      request: action.payload?.request ?? state.request,
    }),

    /**
     * @param {Object} state
     */
    requestGiftFinderLoadMoreAction: (state) => ({ ...state, isLoadingMore: true, loadMoreError: null }),

    /**
     * Append the next page, preserving what is already rendered.
     * @param {Object} state
     * @param {Object} action
     */
    responseGiftFinderLoadMoreAction: (state, action) => {
      const response = action.payload?.response;
      return {
        ...state,
        data: [...(current(state)?.data || []), ...(Array.isArray(response?.data) ? response.data : [])],
        total: response?.total ?? state.total,
        currentPage: response?.currentPage ?? state.currentPage,
        totalPages: response?.totalPages ?? state.totalPages,
        isLoadingMore: false,
        loadMoreError: null,
      };
    },

    /**
     * @param {Object} state
     * @param {Object} action
     */
    failedGiftFinderResultsAction: (state, action) => ({
      ...state,
      isLoading: false,
      isLoaded: true,
      error: action.payload ?? null,
    }),

    /**
     * Load-more failures leave the existing results untouched.
     * @param {Object} state
     * @param {Object} action
     */
    failedGiftFinderLoadMoreAction: (state, action) => ({
      ...state,
      isLoadingMore: false,
      loadMoreError: action.payload ?? null,
    }),
  },
});

export default GiftFinderSlice.reducer;
export const {
  hydrateGiftFinderResultsAction,
  requestGiftFinderResultsAction,
  responseGiftFinderResultsAction,
  requestGiftFinderLoadMoreAction,
  responseGiftFinderLoadMoreAction,
  failedGiftFinderResultsAction,
  failedGiftFinderLoadMoreAction,
} = GiftFinderSlice.actions;
